import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, NgClass } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  OverdraftQuarterlyReviewAccount,
  OverdraftQuarterlyReviewAccountSortType,
  OverdraftQuarterlyReviewAccountStatus,
  OverdraftQuarterlyReviewStatus,
} from '@portal-workspace/grow-shared-library';
import {
  CustomContentLoaderComponent,
  CustomPaginatorComponent,
  PortalHotToastService,
} from '@portal-workspace/grow-ui-library';
import moment from 'moment';
import { interval, Subscription } from 'rxjs';
import { OverdraftQuarterlyReviewService } from '../../../service/overdraft-quarterly-review.service';

const AUTO_REFRESH_MS = 30_000;

interface RetryDialogData {
  title: string;
  message: string;
}

@Component({
  selector: 'overdraft-quarterly-review-retry-dialog',
  standalone: true,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  template: `
    <h2 mat-dialog-title>{{data.title}}</h2>
    <mat-dialog-content>
      <p>{{data.message}}</p>
      <mat-form-field appearance="outline" class="width-100">
        <mat-label>Reason for retry</mat-label>
        <textarea matInput rows="4" maxlength="1000" [formControl]="reason"></textarea>
        <mat-hint align="end">{{reason.value.length}} / 1000</mat-hint>
        @if (reason.touched && !hasValidReason) {
          <mat-error>Reason must be between 3 and 1000 characters</mat-error>
        }
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-flat-button color="primary" [disabled]="!hasValidReason" (click)="submit()">
        Retry
      </button>
    </mat-dialog-actions>
  `,
})
export class OverdraftQuarterlyReviewRetryDialog {
  reason = new FormControl('', {
    nonNullable: true,
    validators: [Validators.maxLength(1000)],
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RetryDialogData,
    private dialogRef: MatDialogRef<OverdraftQuarterlyReviewRetryDialog>,
  ) {}

  get hasValidReason(): boolean {
    const length = this.reason.value.trim().length;
    return length >= 3 && length <= 1000;
  }

  submit(): void {
    this.reason.markAsTouched();
    if (this.hasValidReason) this.dialogRef.close(this.reason.value.trim());
  }
}

@Component({
  selector: 'overdraft-quarterly-review-list',
  templateUrl: './overdraft-quarterly-review-list.component.html',
  styleUrls: ['./overdraft-quarterly-review-list.component.scss'],
  standalone: true,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  imports: [
    CommonModule,
    CustomContentLoaderComponent,
    CustomPaginatorComponent,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    NgClass,
  ],
  providers: [OverdraftQuarterlyReviewService],
})
export class OverdraftQuarterlyReviewListComponent implements OnInit, OnDestroy {
  displayedColumns = [
    'pismoAccountId',
    'accountName',
    'status',
    'currentStep',
    'stepAttemptCount',
    'bankStatementStatus',
    'lastUpdate',
    'actions',
  ];
  runs: OverdraftQuarterlyReviewStatus[] = [];
  selectedRun: OverdraftQuarterlyReviewStatus | null = null;
  dataSource: OverdraftQuarterlyReviewAccount[] = [];
  total = 0;
  limit = 20;
  offset = 0;
  sorts: OverdraftQuarterlyReviewAccountSortType = [{ prop: 'lastUpdate', dir: 'DESC' }];
  sortActive = 'lastUpdate';
  sortDirection: 'asc' | 'desc' | '' = 'desc';
  expandedAccountId: number | null = null;
  statusFilter: OverdraftQuarterlyReviewAccountStatus | 'all' = 'all';
  accountIdFilter = '';
  isLoadingRuns = false;
  isLoadingAccounts = false;
  isRetrying = false;
  hasFilters = false;
  private refreshSubscription?: Subscription;

  readonly statusOptions: Array<{
    value: OverdraftQuarterlyReviewAccountStatus | 'all';
    label: string;
  }> = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'skipped', label: 'Skipped' },
    { value: 'failed', label: 'Failed' },
  ];

  constructor(
    private reviewService: OverdraftQuarterlyReviewService,
    private toastService: PortalHotToastService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadRuns();
    this.refreshSubscription = interval(AUTO_REFRESH_MS).subscribe(() => {
      if (!this.isRetrying && this.selectedRun?.run.status === 'processing') {
        this.loadRuns(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  get selectedRunKey(): string {
    return this.selectedRun
      ? `${this.selectedRun.run.reviewYear}-${this.selectedRun.run.reviewQuarter}`
      : '';
  }

  get canRetryRun(): boolean {
    if (!this.selectedRun || this.isRetrying) return false;
    return this.selectedRun.failedAccounts > 0
      || this.selectedRun.pendingAccounts > 0
      || this.selectedRun.processingAccounts > 0
      || this.selectedRun.run.summaryEmailStatus === 'failed';
  }

  loadRuns(showLoader = true): void {
    const selectedKey = this.selectedRunKey;
    if (showLoader) this.isLoadingRuns = true;
    this.reviewService.getRuns().subscribe({
      next: response => {
        this.runs = response.payload;
        this.selectedRun = this.runs.find(run =>
          `${run.run.reviewYear}-${run.run.reviewQuarter}` === selectedKey,
        ) ?? this.runs[0] ?? null;
        this.isLoadingRuns = false;
        if (this.selectedRun) this.loadAccounts(!showLoader);
        else {
          this.dataSource = [];
          this.total = 0;
        }
      },
      error: () => {
        this.isLoadingRuns = false;
        this.toastService.error('Error loading overdraft quarterly review runs');
      },
    });
  }

  onRunChange(runKey: string): void {
    this.selectedRun = this.runs.find(run =>
      `${run.run.reviewYear}-${run.run.reviewQuarter}` === runKey,
    ) ?? null;
    this.offset = 0;
    this.clearFilters(false);
    this.loadAccounts();
  }

  loadAccounts(silent = false): void {
    if (!this.selectedRun) return;
    if (!silent) this.isLoadingAccounts = true;
    this.reviewService.getAccounts({
      year: this.selectedRun.run.reviewYear,
      quarter: this.selectedRun.run.reviewQuarter,
      page: { limit: this.limit, offset: this.offset },
      filters: {
        status: this.statusFilter === 'all' ? undefined : this.statusFilter,
        pismoAccountId: this.accountIdFilter ? Number(this.accountIdFilter) : undefined,
      },
      sorts: this.sorts,
    }).subscribe({
      next: response => {
        this.dataSource = response.payload;
        this.total = response.total;
        this.isLoadingAccounts = false;
      },
      error: () => {
        this.isLoadingAccounts = false;
        this.toastService.error('Error loading overdraft quarterly review accounts');
      },
    });
  }

  applyFilters(): void {
    const accountId = this.accountIdFilter.trim();
    if (accountId && !/^[1-9]\d*$/.test(accountId)) {
      this.toastService.error('Account ID must be a positive number');
      return;
    }
    this.offset = 0;
    this.hasFilters = this.statusFilter !== 'all' || !!accountId;
    this.loadAccounts();
  }

  clearFilters(reload = true): void {
    this.statusFilter = 'all';
    this.accountIdFilter = '';
    this.hasFilters = false;
    this.offset = 0;
    if (reload) this.loadAccounts();
  }

  onPagination(event: PageEvent): void {
    this.offset = event.pageIndex;
    this.limit = event.pageSize;
    this.loadAccounts();
  }

  onSortData(event: Sort): void {
    this.sortActive = event.active;
    this.sortDirection = event.direction;
    this.sorts = event.direction
      ? [{
        prop: event.active as NonNullable<OverdraftQuarterlyReviewAccountSortType>[number]['prop'],
        dir: event.direction,
      }]
      : [{ prop: 'lastUpdate', dir: 'DESC' }];
    this.offset = 0;
    this.loadAccounts();
  }

  canRetryAccount(account: OverdraftQuarterlyReviewAccount): boolean {
    return account.status === 'failed'
      && !!this.selectedRun
      && ['completed_with_errors', 'failed'].includes(this.selectedRun.run.status)
      && !this.isRetrying;
  }

  retryRun(): void {
    if (!this.selectedRun || !this.canRetryRun) return;
    this.openReasonDialog(
      `Retry ${this.selectedRun.run.reviewYear} Q${this.selectedRun.run.reviewQuarter}`,
      'All unfinished accounts will resume from their saved step. Completed accounts will be preserved.',
      reason => {
        if (!this.selectedRun) return;
        this.isRetrying = true;
        this.reviewService.retryRun({
          year: this.selectedRun.run.reviewYear,
          quarter: this.selectedRun.run.reviewQuarter,
          reason,
        }).pipe(this.toastService.spinnerObservable()).subscribe({
          next: response => {
            this.showRetryResult(response.status, response.message);
            this.finishRetry();
          },
          error: () => {
            this.toastService.error('Error retrying the quarterly review run');
            this.finishRetry();
          },
        });
      },
    );
  }

  retryAccount(account: OverdraftQuarterlyReviewAccount): void {
    if (!this.selectedRun || !this.canRetryAccount(account)) return;
    this.openReasonDialog(
      `Retry account ${account.pismoAccountId}`,
      `The account will resume from the ${this.formatLabel(account.currentStep)} step.`,
      reason => {
        if (!this.selectedRun) return;
        this.isRetrying = true;
        this.reviewService.retryAccount({
          year: this.selectedRun.run.reviewYear,
          quarter: this.selectedRun.run.reviewQuarter,
          pismoAccountId: account.pismoAccountId,
          reason,
        }).pipe(this.toastService.spinnerObservable()).subscribe({
          next: response => {
            this.showRetryResult(response.status, response.message);
            this.finishRetry();
          },
          error: () => {
            this.toastService.error(`Error retrying account ${account.pismoAccountId}`);
            this.finishRetry();
          },
        });
      },
    );
  }

  getStatusClass(status: string): string {
    return `status-${status.replaceAll('_', '-')}`;
  }

  formatLabel(value: string | null): string {
    return value ? value.replaceAll('_', ' ') : '-';
  }

  formatDate(value: Date | string | null): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';
  }

  formatAmount(value: number | null): string {
    return value === null || value === undefined ? '-' : `$${value.toFixed(2)}`;
  }

  formatStoredArray(value: string | null): string {
    if (!value) return '-';
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed) || !parsed.length) return '-';
      return parsed.map(item => typeof item === 'string' ? item : JSON.stringify(item)).join(', ');
    } catch {
      return '-';
    }
  }

  private openReasonDialog(
    title: string,
    message: string,
    action: (reason: string) => void,
  ): void {
    this.dialog.open(OverdraftQuarterlyReviewRetryDialog, {
      width: '520px',
      disableClose: true,
      data: { title, message } satisfies RetryDialogData,
    }).afterClosed().subscribe(reason => {
      if (typeof reason === 'string' && reason) action(reason);
    });
  }

  private showRetryResult(success: boolean, message: string): void {
    success ? this.toastService.success(message) : this.toastService.error(message);
  }

  private finishRetry(): void {
    this.isRetrying = false;
    this.loadRuns();
  }
}
